<script setup lang="ts">
import axios from 'axios'

import SurveyPreview from '../views/survey/Components/SurveyPreview.vue'
import ContestModalPreview from '../views/contests/Components/ContestModalPreview.vue'
import MockupTabletRewards from './MockupTabletRewards.vue'
import CampaignPreview from './CampaignPreview.vue'

import type { CampaignType, TemplateDetail, TemplateLayout, TemplateModule } from '@/types/types'

const route = useRoute()
const { merchantName } = useLocalStorage()
const { t } = useI18n()
const { user } = useLocalStorage()

const props = withDefaults(
  defineProps<{
    module: TemplateModule
    templateDetail: TemplateDetail
    layout: TemplateLayout
  }>(),
  {
    layout: 'emptyState'
  }
)

const languageApiId = import.meta.env.VITE_API_LANGUAGE_ID
const { formatDate } = useDate()

const submodule = computed<CampaignType | null>(() => (route.query.type as CampaignType) || null)
const bgImage = computed(() => `${import.meta.env.VITE_AWS_STORAGE_URL}/empty-state/modale_bg.jpg`)

const template = computed(() => props.templateDetail.templatable)
const goal = ref('')

const rewardColors = ref({
  background: '',
  button: '',
  text: ''
})

const rewardTexts = ref({
  rewardsTitle: '',
  rewardsSubtitle: '',
  rewardsSubtitleNew: '',
  rewardsText: '',
  rewardsButton: '',
  rewardsPopupConfirmTitle: '',
  rewardsPopupConfirmSubtitle: '',
  rewardsPopupForbiddenTitle: ''
})
const countEachRewards = (rewards) => {
  return rewards.reduce((reward, element) => {
    reward[element] = (reward[element] || 0) + 1
    return reward
  }, {})
}
const fetchMerchant = async () => {
  const merchantId = user.value.merchant.id
  const [requestMerchantTexts, requestMerchantColors] = await Promise.all([
    axios.get(`merchants/${merchantId}/texts/${languageApiId}`),
    axios.get(`merchants/${merchantId}/colors`)
  ])
  rewardTexts.value = requestMerchantTexts.data.data
  rewardColors.value = requestMerchantColors.data.data
}

const contestGameRewards: any = ref({})
if (props.module === 'contests') {
  goal.value = t(
    `pages.contests.contestsCreatePage.global.form.fields.boost.${template.value?.contestGameBoostType.slug}.title`
  )

  contestGameRewards.value = countEachRewards(template.value.contestGameRewards.map((i) => i.rewardType.slug))
  if (template.value.canLose) {
    contestGameRewards.value.canLose = template.value.canLose
  }
} else if (props.module === 'loyalty') {
  fetchMerchant()
}
const isPlural = (value: number) => {
  return value > 1 ? 2 : 1
}

const showDetail = computed(
  () => props.layout === 'emptyState' || props.module === 'contests' || props.module === 'campaign'
)
</script>

<template>
  <div
    :class="[
      module === 'survey-question' && 'h-full w-full p-4 sm:px-12 sm:py-28',
      module === 'contests' && 'flex h-[480px] items-center justify-center overflow-hidden p-6',
      module === 'campaign' ? '-mt-20 flex h-96 sm:items-end' : 'flex h-40 sm:items-center'
    ]"
    :style="`background-image: url(${bgImage});`"
    class="sm:block"
  >
    <SurveyPreview
      v-if="module === 'survey-question'"
      class="min-h-[200px] w-full sm:min-h-[360px]"
      :survey-question="template"
    />
    <ContestModalPreview v-if="module === 'contests'" :hide-bottom="false" :scale="0.58" :detail-contests="template" />
    <div v-if="module === 'campaign'" class="w-full overflow-hidden">
      <CampaignPreview
        class="hidden md:block md:h-96"
        :name="merchantName"
        :message="template.messageContent"
        :raw-message="template.messageContent"
        :type="submodule"
        :is-cropped="false"
      />
    </div>
    <div v-if="module === 'loyalty'" class="-ml-48 scale-50 md:-ml-0 lg:scale-75">
      <MockupTabletRewards :reward-colors="rewardColors" :rewards="template.rewards" :reward-texts="rewardTexts" />
    </div>
  </div>
  <div v-if="showDetail" class="flex h-full flex-col p-4 sm:row-span-2 sm:px-12 sm:py-6 lg:col-span-1">
    <p class="text-3xl font-extrabold text-foreground-secondary">{{ templateDetail.name }}</p>
    <p class="mt-4 text-sm text-foreground-secondary">{{ templateDetail.description }}</p>
    <div v-if="module === 'contests'" class="mt-6 text-foreground">
      <p class="text-xs font-medium">{{ t('pages.contests.modal.detail.title') }}</p>
      <div class="mt-3 flex w-max max-w-full gap-2 rounded-lg bg-primary/5 px-3 py-2 text-xs">
        <p class="font-semibold">{{ t('pages.contests.modal.detail.goal') }}</p>
        <p>{{ goal }}</p>
      </div>
      <div class="mt-3 flex w-max max-w-full gap-2 rounded-lg bg-primary/5 px-3 py-2 text-xs">
        <p class="font-semibold">{{ t('pages.contests.modal.detail.daysBetweenParticipations.title') }}</p>
        <p>
          {{
            template.daysBetweenParticipations > 0
              ? t('pages.contests.modal.detail.daysBetweenParticipations.everyday', {
                  day: template.daysBetweenParticipations
                })
              : t('pages.contests.modal.detail.daysBetweenParticipations.once')
          }}
        </p>
      </div>
      <div
        v-if="Object.keys(contestGameRewards).length > 0"
        class="mt-3 flex w-max max-w-full gap-2 rounded-lg bg-primary/5 px-3 py-2 text-xs"
      >
        <p class="font-semibold">{{ t('pages.contests.modal.detail.rewardType.title') }}</p>
        <span v-for="(value, name) in contestGameRewards" :key="name">
          {{ t(`pages.contests.modal.detail.rewardType.types.${name}`, { count: value }, isPlural(value)) }}</span
        >
      </div>
    </div>
    <div v-if="module === 'campaign'" class="mt-6 text-foreground">
      <p class="text-xs font-medium">{{ t('pages.contests.modal.detail.title') }}</p>
      <div class="mt-3 flex max-w-full flex-col gap-2 rounded-lg bg-primary/5 px-3 py-2 text-xs sm:w-max">
        <p class="font-semibold">{{ t(`pages.${module}.modal.detail.messageContent`) }}</p>
        <p>
          {{ template.messageContent }}
        </p>
      </div>
      <div
        v-if="templateDetail.prefilledDate"
        class="mt-3 flex w-max max-w-full gap-2 rounded-lg bg-primary/5 px-3 py-2 text-xs"
      >
        <p class="font-semibold">{{ t(`pages.${module}.modal.detail.launchDate`) }}</p>
        <p>
          {{ formatDate({ date: templateDetail.prefilledDate, toFormat: 'DD MMM YYYY' }) }}
        </p>
      </div>
    </div>
    <div v-if="module === 'loyalty'" class="mt-6 text-foreground">
      <p class="text-xs font-medium">{{ t('pages.contests.modal.detail.title') }}</p>
      <div class="mt-3 flex w-max max-w-full gap-2 rounded-lg bg-primary/5 px-3 py-2 text-xs">
        <p class="font-semibold">{{ t('pages.loyalty.modal.detail.rewards.title') }}</p>
        <p>
          {{
            t(
              `pages.loyalty.modal.detail.rewards.description`,
              { count: template.rewards.length },
              isPlural(template.rewards.length)
            )
          }}
        </p>
      </div>
    </div>
  </div>
</template>
